import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Pencil, Trash2, MapPin } from '@/lib/icons';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setSelectedTenantId } from '@/features/tenants/tenant-slice';
import { Button } from '@/components/ui/button';
import { ActionButton } from '@/components/ui/action-button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import {
  Branch,
  createBranch,
  updateBranch,
  deleteBranch,
} from '@/features/branches/api';
import {
  useBranchesQuery,
  useInvalidateBranches,
} from '@/features/branches/queries';
import { getTenantOptions } from '@/features/value-lists/api';
import { isSuperAdmin } from '@/lib/auth/roles';

export const Route = createFileRoute('/_layout/settings/branches')({
  component: BranchesPage,
});

const createBranchFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(2, t('branches.form.validation.nameRequired')),
    address: z.string().optional(),
    city: z.string().optional(),
    phone: z.string().optional(),
    status: z.enum(['active', 'inactive']).optional(),
  });

type BranchFormValues = z.infer<ReturnType<typeof createBranchFormSchema>>;

const statusBadgeVariant = (status: string) => {
  switch (status) {
    case 'active':
      return 'success';
    default:
      return 'secondary';
  }
};

function BranchesPage() {
  const { t } = useTranslation();
  const BranchFormSchema = createBranchFormSchema(t);
  const { toast } = useToast();
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const selectedTenantId = useAppSelector(
    (state) => state.tenant.selectedTenantId
  );
  const invalidateBranches = useInvalidateBranches();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [deletingBranch, setDeletingBranch] = useState<Branch | null>(null);

  const isCrossTenantOperator =
    isSuperAdmin(user) && !user?.tenantId;

  // Access (super_admin / tenant_admin) is enforced by the _layout beforeLoad guard.

  // Fetch tenants for Super Admin selector
  const { data: tenantsData } = useQuery({
    queryKey: ['tenant-options'],
    queryFn: getTenantOptions,
    enabled: isCrossTenantOperator,
  });

  const tenants = tenantsData?.value ?? [];

  // Shared with every other branch consumer, and keyed on the active tenant —
  // see features/branches/queries. Editing here has to refresh the filters and
  // pickers elsewhere, which a screen-local key silently failed to do.
  const { data, isLoading, isError, error, refetch } = useBranchesQuery({
    enabled: isCrossTenantOperator ? !!selectedTenantId : true,
  });

  const branches = data?.value ?? [];

  const form = useForm<BranchFormValues>({
    resolver: zodResolver(BranchFormSchema),
    defaultValues: {
      name: '',
      address: '',
      city: '',
      phone: '',
      status: 'active',
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (values: BranchFormValues) => {
      if (editingBranch) {
        return updateBranch(editingBranch.id, {
          name: values.name,
          address: values.address ?? null,
          city: values.city ?? null,
          phone: values.phone ?? null,
          status: values.status,
        });
      }
      return createBranch({
        name: values.name,
        address: values.address ?? null,
        city: values.city ?? null,
        phone: values.phone ?? null,
      });
    },
    onSuccess: (result) => {
      if (result.succeeded) {
        invalidateBranches();
        toast({
          title: t('common.success'),
          description: editingBranch
            ? t('branches.toast.updated')
            : t('branches.toast.created'),
        });
        closeDialog();
      } else {
        toast({
          title: t('common.error'),
          description: result.errors?.[0] ?? t('branches.toast.error'),
          variant: 'destructive',
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return deleteBranch(id);
    },
    onSuccess: (result) => {
      if (result.succeeded) {
        invalidateBranches();
        toast({
          title: t('common.success'),
          description: t('branches.toast.deleted'),
        });
      } else {
        toast({
          title: t('common.error'),
          description: result.errors?.[0] ?? t('branches.toast.error'),
          variant: 'destructive',
        });
      }
      setDeleteDialogOpen(false);
      setDeletingBranch(null);
    },
  });

  const openCreate = () => {
    setEditingBranch(null);
    form.reset({
      name: '',
      address: '',
      city: '',
      phone: '',
      status: 'active',
    });
    setDialogOpen(true);
  };

  const openEdit = (branch: Branch) => {
    setEditingBranch(branch);
    form.reset({
      name: branch.name,
      address: branch.address ?? '',
      city: branch.city ?? '',
      phone: branch.phone ?? '',
      status: branch.status,
    });
    setDialogOpen(true);
  };

  const openDelete = (branch: Branch) => {
    setDeletingBranch(branch);
    setDeleteDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingBranch(null);
    form.reset();
  };

  const onSubmit = (values: BranchFormValues) => {
    saveMutation.mutate(values);
  };

  const handleTenantChange = (value: string) => {
    dispatch(setSelectedTenantId(value));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('branches.title')}</h1>
          <p className="text-muted-foreground text-sm">
            {t('branches.description')}
          </p>
        </div>
        <Button
          onClick={openCreate}
          disabled={isCrossTenantOperator && !selectedTenantId}
        >
          <Plus className="w-4 h-4 mr-2" /> {t('branches.addBranch')}
        </Button>
      </div>

      {/* Tenant selector for Super Admin */}
      {isCrossTenantOperator && (
        <div className="max-w-sm">
          <Select
            value={selectedTenantId ?? ''}
            onValueChange={handleTenantChange}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('branches.selectTenant')} />
            </SelectTrigger>
            <SelectContent>
              {tenants.map((tenant) => (
                <SelectItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!selectedTenantId && (
            <p className="text-sm text-muted-foreground mt-1">
              {t('branches.selectTenantDescription')}
            </p>
          )}
        </div>
      )}

      {/* Show prompt if Super Admin hasn't selected a tenant */}
      {isCrossTenantOperator && !selectedTenantId ? (
        <div className="rounded-xl overflow-hidden">
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <MapPin className="w-8 h-8 mb-2 opacity-30" />
            <p className="font-medium">{t('branches.selectTenant')}</p>
            <p className="text-sm">{t('branches.selectTenantDescription')}</p>
          </div>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('branches.columns.name')}</TableHead>
                <TableHead>{t('branches.columns.address')}</TableHead>
                <TableHead>{t('branches.columns.city')}</TableHead>
                <TableHead>{t('branches.columns.phone')}</TableHead>
                <TableHead>{t('branches.columns.status')}</TableHead>
                <TableHead className="text-right">
                  {t('common.actionsTitle')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-5 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-40" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-16" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="h-5 w-16 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={6} className="p-0">
                    <div className="p-6">
                      <ApiErrorCard
                        error={error}
                        onRetry={() => refetch()}
                        title="Failed to load branches"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ) : branches.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-12 text-muted-foreground"
                  >
                    <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="font-medium">{t('branches.empty.title')}</p>
                    <p className="text-sm">{t('branches.empty.description')}</p>
                  </TableCell>
                </TableRow>
              ) : (
                branches.map((branch) => (
                  <TableRow
                    key={branch.id}
                    className="hover:bg-muted/30 transition-colors"
                  >
                    <TableCell className="font-medium">{branch.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {branch.address ?? '\u2014'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {branch.city ?? '\u2014'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {branch.phone ?? '\u2014'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          statusBadgeVariant(branch.status) as
                            | 'success'
                            | 'secondary'
                        }
                      >
                        {t(`branches.status.${branch.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(branch)}
                          className="h-8 w-8"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDelete(branch)}
                          className="h-8 w-8 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingBranch
                ? t('branches.form.editTitle')
                : t('branches.form.createTitle')}
            </DialogTitle>
            <DialogDescription>
              {editingBranch
                ? t('branches.form.editDescription')
                : t('branches.form.createDescription')}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('branches.form.name')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('branches.form.namePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('branches.form.address')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('branches.form.addressPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('branches.form.city')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('branches.form.cityPlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t('branches.form.phone')}</FormLabel>
                      <FormControl>
                        <PhoneInput
                          name={field.name}
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          invalid={!!fieldState.error}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {editingBranch && (
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('branches.columns.status')}</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="active">
                            {t('branches.status.active')}
                          </SelectItem>
                          <SelectItem value="inactive">
                            {t('branches.status.inactive')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeDialog}>
                  {t('common.actions.cancel')}
                </Button>
                <ActionButton type="submit" loading={saveMutation.isPending}>
                  {editingBranch
                    ? t('common.actions.save')
                    : t('common.actions.create')}
                </ActionButton>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('branches.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('branches.delete.description', {
                name: deletingBranch?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingBranch(null)}>
              {t('common.actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deletingBranch && deleteMutation.mutate(deletingBranch.id)
              }
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending
                ? t('common.actions.deleting')
                : t('common.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
